import React, { useState, Fragment, useEffect, useMemo } from 'react'
import injectSheet from 'react-jss'
import Head from '../../head/head'
import BackToPupils from '../../back-to-pupils/back-to-pupils'
import HeadTitle from '../../head-title/head-title'
import Paper from 'pobble-components/dist/paper'
import classNames from 'classnames'
import FormLabel from 'pobble-components/dist/form-elements/form-label'
import FormInput from 'pobble-components/dist/form-elements/form-input'
import FormRow from 'pobble-components/dist/form-elements/form-row'
import FormSelect from 'pobble-components/dist/form-elements/form-select'
import { apiGetYearGroups } from '../../../common/api/year-groops'
import { errorHandler } from 'pobble-components/dist/error-handler'
import Bone from 'pobble-components/dist/bone'
import Button from 'pobble-components/dist/button'
import {
  apiCreateStudent,
  apiParseCsvOfStudents
} from '../../../common/api/student-management'
import { useMe } from 'pobble-components/dist/pobble-user'
import { Modal, ModalContent } from 'pobble-components/dist/modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave } from '@fortawesome/pro-solid-svg-icons/faSave'
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons/faTimesCircle'
import { faPrint } from '@fortawesome/pro-solid-svg-icons/faPrint'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle'
import pupilsImg from './pupils.png'
import FormFile from 'pobble-components/dist/form-elements/form-file'
import { URLS } from 'pobble-components/dist/setup'
import ButtonElement from 'pobble-components/dist/button-element'
import printPasscodesImg from '../../../common/images/print-passcodes.png'
import { usePasscodesPrint } from '../../print-passcodes/print-passcodes'
import Tooltip from 'pobble-components/dist/tooltip'
import PobbleLink from 'pobble-components/dist/pobble-link'
import { Link } from 'react-router-dom'

const ResultModal = injectSheet(({ FONT_SIZES, COLORS }) => ({
  content: {
    minWidth: '460px',
    maxWidth: '460px',
    padding: '0',
    '@media (max-width: 575px)': {
      minWidth: 'auto'
    }
  },
  img: {
    borderRadius: '6px 6px 0 0',
    overflow: 'hidden',
    padding: '0 0 54.5%',
    position: 'relative',
    '& img': {
      position: 'absolute',
      left: '0',
      top: '0',
      width: '100%'
    }
  },
  desc: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '30px',
    textAlign: 'center',
    '& h2': {
      color: COLORS.TEXT.MID_GREY.NORMAL,
      ...FONT_SIZES.HEADING_BIG.STYLE,
      margin: '0 0 10px',
      '@media (min-width: 541px)': {
        ...FONT_SIZES.HEADING_LARGE.STYLE
      }
    },
    '& p': {
      margin: '0'
    }
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '20px',
    '& >:first-child': {
      marginBottom: '10px'
    }
  },
  closeButton: {
    fontSize: '17px',
    right: '20px',
    top: '20px',
    color: '#fff',
    position: 'absolute',
    zIndex: '100'
  },
  hint: {
    ...FONT_SIZES.BODY_SMALL.STYLE,
    color: COLORS.TEXT.MID_GREY.NORMAL,
    margin: '6px 0'
  },
  link: {
    textDecoration: 'none'
  }
}))(({ classes, onPrint, accounts, ...props }) => {
  const { pupils, printInProgress, PrintComponent, print } = usePasscodesPrint({
    accounts,
    onAfterPrint: () => props.requestClose()
  })

  return (
    <Modal {...props}>
      <ModalContent legacy className={classes.content}>
        <ButtonElement
          onClick={props.requestClose}
          className={classes.closeButton}>
          <FontAwesomeIcon icon={faTimesCircle} />
        </ButtonElement>
        <div className={classes.desc}>
          <h2>Your pupils have been saved</h2>
          <div className={classes.buttons}>
            <Link className={classes.link} to={'/'}>
              <PobbleLink>Close and return to My Pupils</PobbleLink>
            </Link>
          </div>
        </div>
        <PrintComponent />
      </ModalContent>
    </Modal>
  )
})

const LayoutHolder = injectSheet({
  holder: {
    maxWidth: '780px',
    margin: '0 auto',
    padding: '0 20px'
  }
})(({ classes, className, children, ...props }) => (
  <div className={classNames(classes.holder, className)} {...props}>
    {children}
  </div>
))

const PupilsTable = injectSheet(({ COLORS }) => ({
  info: {
    color: '#ccc',
    margin: '0 3px 0 0'
  },
  ageLabel: {
    display: 'flex',
    alignItems: 'center'
  },
  labelRow: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  holder: {
    '@media (min-width: 541px)': {
      display: 'grid',
      width: '100%',
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: '20px',
      '& > *': {
        margin: '0'
      }
    }
  },
  title: {
    fontSize: '18px',
    lineHeight: '22px',
    color: COLORS.TEXT.MID_GREY.NORMAL,
    marginTop: '5px'
  },
  intro: {
    marginBottom: '30px'
  },
  link: {
    textDecoration: 'none',
    fontWeight: 'bold',
    color: COLORS.BLUE.NORMAL
  },
  separator: {
    margin: '25px -20px 20px',
    borderBottom: '1px solid #ededed',
    '@media (min-width: 541px)': {
      display: 'none'
    }
  }
}))(({ data, classes, onChange, yearGroups, canEnableProvider }) => {
  const { me } = useMe()
  return (
    <Fragment>
      <div className={classes.intro}>
        <h3 className={classes.title}>Recommended for individual classes</h3>
        <p>
          Manually add your pupils below via the form below, or via CSV, then
          manually&nbsp;
          <Link className={classes.link} to={'/groups'}>
            create your groups
          </Link>
          .
          {me.account.policy.view_school_tab && (
            <span>
              {' '}
              Alternatively,{' '}
              <PobbleLink href="/account/school/data">
                sync your pupils and groups to Pobble
              </PobbleLink>{' '}
              from your MIS via our partner Wonde.
            </span>
          )}
        </p>
      </div>
      <div className={classes.holder}>
        {data.map((item, index) => {
          return (
            <Fragment key={index}>
              {!!index && <div className={classes.separator} />}
              <FormRow>
                <FormLabel required small>
                  First Name
                </FormLabel>
                <FormInput
                  required={!item.isEmpty}
                  value={item.fields.firstName || ''}
                  onChange={e => {
                    onChange(index, 'firstName', e.target.value)
                  }}
                  placeholder="Add first name"
                />
              </FormRow>
              <FormRow>
                <FormLabel required small>
                  Last Name
                </FormLabel>
                <FormInput
                  required={!item.isEmpty}
                  value={item.fields.lastName || ''}
                  onChange={e => {
                    onChange(index, 'lastName', e.target.value)
                  }}
                  placeholder="Add last name"
                />
              </FormRow>
              <FormRow>
                <FormLabel className={classes.ageLabel} required small>
                  <Tooltip
                    className={classes.info}
                    content="Please assign pupils to the age group they belong to this school year e.g. for Year 6 use age 10-11.">
                    <span>
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </span>
                  </Tooltip>
                  Age Group
                </FormLabel>

                {yearGroups && (
                  <FormSelect
                    required={!item.isEmpty}
                    value={item.fields.ageGroup || ''}
                    onChange={e => {
                      onChange(index, 'ageGroup', e.target.value)
                    }}>
                    <option value={''} default>
                      Select age group
                    </option>
                    {yearGroups.map(yg => {
                      return (
                        <option key={yg.id} value={yg.id}>
                          {yg.title}
                        </option>
                      )
                    })}
                  </FormSelect>
                )}
                {!yearGroups && <Bone btn />}
              </FormRow>
            </Fragment>
          )
        })}
      </div>
    </Fragment>
  )
})

const ActionBar = injectSheet({
  container: {
    height: '80px',
    position: 'fixed',
    bottom: '0',
    background: '#fff',
    boxShadow: '0 -2px 4px 0 rgba(0, 0, 0, 0.1)',
    width: '100%',
    transform: 'translateY(100px)',
    transition: 'transform .3s'
  },
  show: {
    transform: 'translateY(0)'
  },
  holder: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: '100%',
    '& span': {
      marginRight: '16px'
    }
  }
})(({ classes, data, pending }) => {
  return (
    <div
      className={classNames(classes.container, {
        [classes.show]: !!data.length
      })}>
      <LayoutHolder className={classes.holder}>
        <span>
          You are about to add {data.length} pupil
          {data.length > 1 && 's'}
        </span>
        <Button
          tall
          type="submit"
          pending={pending}
          icon={<FontAwesomeIcon icon={faSave} />}>
          Save pupils
        </Button>
      </LayoutHolder>
    </div>
  )
})

const addFormItemDetails = item => {
  const filledFileds = Object.keys(item.fields).filter(
    key => !!item.fields[key]
  ).length
  item.isEmpty = !item.fields.firstName && !item.fields.lastName
  item.isComplete = filledFileds === 3
  return item
}

const AddPupils = injectSheet(({ COLORS, FONT_SIZES }) => ({
  container: {
    paddingBottom: '100px'
  },
  head: {
    height: 'auto',
    padding: '20px 0',
    position: 'relative'
  },
  backToPupils: {
    position: 'absolute',
    left: '20px',
    top: '30px'
  },
  card: {
    borderRadius: '6px',
    background: '#fff',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    margin: '0 0 30px',
    '& img': {
      height: '200px',
      width: '100%',
      objectFit: 'cover',
      '@media (min-width: 541px)': {
        width: '176px',
        height: 'auto'
      }
    },
    '@media (min-width: 541px)': {
      display: 'flex',
      flexDirection: 'row-reverse'
    }
  },
  mobileBreak: {
    '@media (min-width: 541px)': {
      display: 'none'
    }
  },
  cardDesc: {
    flex: '1',
    padding: '22px',
    '& h2': {
      ...FONT_SIZES.HEADING_MEDIUM.STYLE,
      color: COLORS.TEXT.MID_GREY.NORMAL,
      margin: '0 0 20px',
      '@media (min-width: 541px)': {
        ...FONT_SIZES.HEADING_BIG.STYLE
      }
    },
    '& ul': {
      margin: '0',
      padding: '0',
      listStyleType: 'none',
      '& li': {
        marginBottom: '16px',
        position: 'relative',
        padding: '0 0 0 20px',
        '&:after': {
          content: '""',
          position: 'absolute',
          left: '0',
          top: '6px',
          width: '5px',
          height: '5px',
          borderRadius: '5px',
          background: COLORS.BLUE.NORMAL
        }
      }
    }
  },
  safetyHint: {
    margin: '24px 0',
    color: 'var(--grey)',
    font: 'var(--body-)'
  },
  csv: {
    marginTop: '20px',
    color: COLORS.TEXT.MID_GREY.NORMAL,
    '& a': {
      color: COLORS.TEXT.MID_GREY.NORMAL,
      textDecoration: 'underline'
    },
    '& label': {
      cursor: 'pointer',
      color: COLORS.BLUE.NORMAL,
      fontWeight: 'bold'
    }
  }
}))(({ classes }) => {
  const [data, setData] = useState([{ fields: {}, isEmpty: true }])
  const [yearGroups, setYearGroups] = useState(null)
  const [savedPupils, setSavedPupils] = useState([])
  const [resultModalState, setResultModalState] = useState({
    open: false
  })
  const [addingPupils, setAddingPupils] = useState(false)
  const finalData = useMemo(
    () => {
      return data.filter(item => !item.isEmpty)
    },
    [data]
  )

  const { me } = useMe()

  const schoolId = me.account.teacher_type.school.id
  const canEnableProvider = me.account.policy.enable_pull_provider

  useEffect(() => {
    apiGetYearGroups({
      include: 'year_group.names,year_group.order'
    })
      .then(res => setYearGroups(res.data.year_groups))
      .catch(errorHandler)
  }, [])

  return (
    <div className={classes.container}>
      <Head className={classes.head}>
        <BackToPupils className={classes.backToPupils} />
        <HeadTitle>Add pupils</HeadTitle>
      </Head>
      <LayoutHolder>
        <div className={classes.card}>
          <img src={pupilsImg} alt="Students in class" />
          <div className={classes.cardDesc}>
            <h2>Add your pupils to Pobble if you are planning to...</h2>
            <ul>
              <li>publish your children's writing.</li>
              <li>create writing moderation files.</li>
              <li>use our pupil logins so children can access Pobble.</li>
            </ul>
          </div>
        </div>
      </LayoutHolder>
      <form
        onSubmit={e => {
          e.preventDefault()
          setAddingPupils(true)
          Promise.all(
            finalData.map(item => {
              return apiCreateStudent(
                schoolId,
                {
                  first_name: item.fields.firstName,
                  last_name: item.fields.lastName,
                  year_group_id: item.fields.ageGroup
                },
                {
                  include: 'account.names,account.student_type.passcode'
                }
              ).catch(e => false)
            })
          ).then(result => {
            setAddingPupils(false)
            const errors = []
            const saved = []
            result.forEach((item, index) => {
              if (!item) {
                errors.push(data[index])
              } else {
                saved.push(item.data.account)
              }
            })
            setResultModalState({
              open: true
            })
            setSavedPupils(saved)
            setData(!errors.length ? [{ fields: {}, isEmpty: true }] : errors)
          })
        }}>
        <LayoutHolder>
          <Paper>
            <PupilsTable
              yearGroups={yearGroups}
              data={data}
              canEnableProvider={canEnableProvider}
              onChange={(i, field, value) => {
                const dataCopy = [...data]
                const dataItem = dataCopy[i]
                dataItem.fields[field] = value
                addFormItemDetails(dataItem)
                if (dataItem.isComplete && !data[i + 1]) {
                  dataCopy[i + 1] = {
                    fields: { ageGroup: dataItem.fields.ageGroup },
                    isEmpty: true
                  }
                }
                setData(dataCopy)
              }}
            />
            <div className={classes.csv}>
              Or use{' '}
              <a href={`${URLS.DASHBOARD}/files/PobbleStudentTemplate.csv`}>
                our template
              </a>{' '}
              to&nbsp;
              <FormFile
                onChange={files => {
                  if (!files.length) return
                  apiParseCsvOfStudents({
                    school_id: schoolId,
                    student_details_csv: files[0]
                  }).then(res => {
                    const newData = res.data.student_details.map(item =>
                      addFormItemDetails({
                        fields: {
                          firstName: item.first_name,
                          lastName: item.last_name,
                          ageGroup: item.year_group_id
                        }
                      })
                    )
                    const oldData = data.slice(0, data.length - 1)
                    const lastField = data[data.length - 1]
                    setData([...oldData, ...newData, lastField])
                  })
                }}
                accept=".csv">
                <span>Upload CSV file</span>
              </FormFile>
            </div>
          </Paper>
          {me.account.policy.view_school_tab && (
            <p className={classes.safetyHint}>
              Pobble is a safe space. You can always review who has access to
              your pupil data on your{' '}
              <PobbleLink href="/account/school/team">Team page</PobbleLink>
            </p>
          )}
        </LayoutHolder>
        <ActionBar pending={addingPupils} data={finalData} />
      </form>
      <ResultModal
        accounts={savedPupils}
        isOpen={resultModalState.open}
        requestClose={() =>
          setResultModalState({ ...resultModalState, open: false })
        }
      />
    </div>
  )
})

export default AddPupils
